/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ChangeEvent, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import ListNumberedIcon from '@atlaskit/icon/core/list-numbered';
import { Panel } from '@atlaskit/navigation-system';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { Switcher } from '@atlassian/twg-common';

import { usePanelContext } from '../../../hooks/use-panel-context';
import {
	cloudIdAtom,
	orgIdAtom,
	orgMetricsSourceAtom,
	selectedStaticDatasetKeyAtom,
} from '../../state';

import { messages } from './messages';
import { STATIC_DATASET_LIST, type StaticDatasetKey } from './staticDatasets';

const styles = cssMap({
	panel: {
		paddingInline: token('space.300'),
		paddingBlock: token('space.300'),
	},
	heading: {
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
	},
	text: {
		font: token('font.body'),
		fontFamily: token('font.family.brand.body'),
	},
	list: {
		font: token('font.body'),
		fontFamily: token('font.family.brand.body'),
		paddingLeft: token('space.300'),
		marginBottom: token('space.400'),
	},
	tabContent: {
		marginTop: token('space.300'),
	},
	helperText: {
		font: token('font.body.small'),
		fontFamily: token('font.family.brand.body'),
		color: token('color.text.subtlest'),
		marginBottom: token('space.200'),
	},
	datasetList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	datasetItem: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		paddingBlock: token('space.150'),
		// Reserve room on the right so the absolutely-positioned check icon
		// never overlaps the dataset label/description text.
		paddingLeft: token('space.200'),
		paddingRight: token('space.500'),
		borderRadius: token('radius.small'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface.raised'),
		textAlign: 'left',
		cursor: 'pointer',
		color: token('color.text'),
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	datasetItemSelected: {
		border: `${token('border.width')} solid ${token('color.border.selected')}`,
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
	datasetCheckIcon: {
		position: 'absolute',
		top: token('space.150'),
		right: token('space.150'),
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	datasetLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.brand.body'),
		fontWeight: token('font.weight.semibold'),
	},
	datasetDescription: {
		font: token('font.body.small'),
		fontFamily: token('font.family.brand.body'),
		color: token('color.text.subtlest'),
	},
	datasetDescriptionSelected: {
		color: token('color.text.selected'),
	},
	collapsibleSections: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	fieldLabel: {
		display: 'block',
		font: token('font.body.small'),
		fontFamily: token('font.family.brand.body'),
		fontWeight: token('font.weight.semibold'),
		marginBottom: token('space.050'),
	},
	field: {
		marginBottom: token('space.200'),
	},
	submitContainer: {
		marginTop: token('space.200'),
	},
	resetContainer: {
		marginTop: token('space.400'),
		paddingTop: token('space.300'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		display: 'flex',
		justifyContent: 'flex-end',
	},
	// Accordion
	accordion: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	headerContainer: {
		position: 'relative',
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		textAlign: 'left',
		gap: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		width: '100%',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			cursor: 'pointer',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			cursor: 'pointer',
		},
		display: 'flex',
		alignItems: 'center',
	},
	collapsibleContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		paddingInline: token('space.200'),
	},
	featureGateRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.200'),
	},
	featureGateLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.brand.body'),
		fontWeight: token('font.weight.semibold'),
	},
});

type TabKey = 'static' | 'custom';

type CustomFormProps = {
	onSubmit: (cloudId: string, orgId: string) => void;
};

function CustomDataForm({ onSubmit }: CustomFormProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [cloudIdInput, setCloudIdInput] = useState<string>('');
	const [orgIdInput, setOrgIdInput] = useState<string>('');

	const trimmedCloudId = cloudIdInput.trim();
	const trimmedOrgId = orgIdInput.trim();
	const isSubmitDisabled = trimmedCloudId.length === 0 || trimmedOrgId.length === 0;

	const handleSubmit = () => {
		if (isSubmitDisabled) {
			return;
		}
		onSubmit(trimmedCloudId, trimmedOrgId);
	};

	return (
		<div css={styles.tabContent}>
			<div css={styles.field}>
				<label htmlFor="debug-panel-custom-cloud-id" css={styles.fieldLabel}>
					{formatMessage(messages.cloudIdLabel)}
				</label>
				<Textfield
					id="debug-panel-custom-cloud-id"
					name="cloudId"
					value={cloudIdInput}
					placeholder={formatMessage(messages.cloudIdPlaceholder)}
					isRequired
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setCloudIdInput(event.currentTarget.value)
					}
					testId="debug-panel-custom-cloud-id"
				/>
			</div>
			<div css={styles.field}>
				<label htmlFor="debug-panel-custom-org-id" css={styles.fieldLabel}>
					{formatMessage(messages.orgIdLabel)}
				</label>
				<Textfield
					id="debug-panel-custom-org-id"
					name="orgId"
					value={orgIdInput}
					placeholder={formatMessage(messages.orgIdPlaceholder)}
					isRequired
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setOrgIdInput(event.currentTarget.value)
					}
					testId="debug-panel-custom-org-id"
				/>
			</div>
			<div css={styles.submitContainer}>
				<Button
					appearance="primary"
					isDisabled={isSubmitDisabled}
					onClick={handleSubmit}
					testId="debug-panel-custom-submit"
				>
					{formatMessage(messages.viewDataButton)}
				</Button>
			</div>
		</div>
	);
}

type StaticDataListProps = {
	selectedKey: StaticDatasetKey | null;
	onSelect: (key: StaticDatasetKey) => void;
};

function StaticDataList({ selectedKey, onSelect }: StaticDataListProps): JSX.Element {
	const { formatMessage } = useIntl();
	return (
		<div css={styles.tabContent}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.helperText}>{formatMessage(messages.staticDataHelperText)}</p>
			<div css={styles.datasetList}>
				{STATIC_DATASET_LIST.map((dataset) => {
					const isSelected = dataset.key === selectedKey;
					return (
						<button
							key={dataset.key}
							type="button"
							css={[styles.datasetItem, isSelected && styles.datasetItemSelected]}
							onClick={() => onSelect(dataset.key)}
							aria-pressed={isSelected}
							data-testid={`debug-panel-static-dataset-${dataset.key}`}
						>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span css={styles.datasetLabel}>{formatMessage(dataset.label)}</span>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span
								css={[styles.datasetDescription, isSelected && styles.datasetDescriptionSelected]}
							>
								{formatMessage(dataset.description)}
							</span>
							{isSelected ? (
								<span css={styles.datasetCheckIcon} aria-hidden="true">
									<CheckCircleIcon
										label=""
										color={token('color.icon.selected')}
										spacing="spacious"
									/>
								</span>
							) : null}
						</button>
					);
				})}
			</div>
		</div>
	);
}

type CollapsibleSectionProps = {
	header: string;
	/** The content can have one or more children. However, each top level child should be wrapped by `IndentedContent` element so it has proper indentation on the left side. */
	content?: React.ReactNode;
	/** Default: false */
	defaultOpen?: boolean;
};

function CollapsibleSection({ header, content, defaultOpen }: CollapsibleSectionProps) {
	const [expanded, setExpanded] = useState(defaultOpen);

	return (
		<Box xcss={styles.accordion}>
			<Pressable xcss={styles.headerContainer} onClick={() => setExpanded((_) => !_)} tabIndex={-1}>
				<IconButton
					appearance="subtle"
					icon={(iconProps) => {
						const IconComponent = !expanded ? ChevronRightIcon : ChevronDownIcon;
						return <IconComponent {...iconProps} size="small" />;
					}}
					label={expanded ? 'Collapse' : 'Expand'}
					spacing="compact"
				/>

				<Heading size="small">{header}</Heading>
			</Pressable>

			{expanded && content && <Box>{content}</Box>}
		</Box>
	);
}

const EXPLORATION_V2_GATE = 'twg_microsite_exploration_v2';

/**
 * Reads the current effective value of `twg_microsite_exploration_v2`.
 *
 * Mirrors the resolution order used by `FeatureGates.checkGate` /
 * `studioFg`: a manual override in `STATSIG_OVERRIDES` localStorage wins,
 * otherwise we fall back to whatever the platform-feature-flags
 * resolver currently reports for the gate.
 */
function readExplorationV2Value(): boolean {
	const overrides = FeatureGates.getOverrides();
	if (EXPLORATION_V2_GATE in overrides.gates) {
		return overrides.gates[EXPLORATION_V2_GATE];
	}
	return fg('twg_microsite_exploration_v2');
}

function FeatureGatesSection(): JSX.Element {
	const { formatMessage } = useIntl();
	const [isExplorationV2On, setIsExplorationV2On] = useState<boolean>(() =>
		readExplorationV2Value(),
	);

	const handleExplorationV2Change = (event: ChangeEvent<HTMLInputElement>) => {
		const nextValue = event.currentTarget.checked;
		// Persist the override to STATSIG_OVERRIDES so any subsequent
		// `fg()` / `FeatureGates.checkGate` call (in this tab and any
		// future page load) sees the new value.
		FeatureGates.overrideGate(EXPLORATION_V2_GATE, nextValue);
		setIsExplorationV2On(nextValue);
		// `fg('twg_microsite_exploration_v2')` is read once at
		// `LoggedInGate` render time to decide between LoggedInView and
		// LoggedInViewV2, so a hard reload is the simplest way to pick
		// up the new value across the whole tree.
		window.location.reload();
	};

	return (
		<div css={styles.collapsibleContent}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.helperText}>{formatMessage(messages.explorationV2SectionHelper)}</p>
			<div css={styles.featureGateRow}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<span css={styles.featureGateLabel}>
					{formatMessage(messages.explorationV2SectionTitle)}
				</span>
				<Toggle
					id="debug-panel-exploration-v2-toggle"
					label={formatMessage(messages.explorationV2ToggleLabel)}
					isChecked={isExplorationV2On}
					onChange={handleExplorationV2Change}
					testId="debug-panel-exploration-v2-toggle"
				/>
			</div>
		</div>
	);
}

export function DebugPanel(): JSX.Element | null {
	const { formatMessage } = useIntl();
	const { isOpen } = usePanelContext();
	const [selectedTab, setSelectedTab] = useState<TabKey>('static');
	const [, setCloudId] = useAtom(cloudIdAtom);
	const [, setOrgId] = useAtom(orgIdAtom);
	const [orgMetricsSource, setOrgMetricsSource] = useAtom(orgMetricsSourceAtom);
	const [selectedStaticKey, setSelectedStaticKey] = useAtom(selectedStaticDatasetKeyAtom);

	if (!isOpen) {
		return null;
	}

	const handleStaticSelect = (key: StaticDatasetKey) => {
		setSelectedStaticKey(key);
		setOrgMetricsSource('static');
	};

	const handleCustomSubmit = (cloudId: string, orgId: string) => {
		setCloudId(cloudId);
		setOrgId(orgId);
		setOrgMetricsSource('api');
		// Custom data takes over — make sure the static list reflects the
		// switch by clearing any previously highlighted scenario.
		setSelectedStaticKey(null);
	};

	const handleReset = () => {
		// Atom state lives only in memory, so a hard reload returns the panel
		// (and the rest of the page) to its initial, untouched configuration.
		window.location.reload();
	};

	const activeStaticKey = orgMetricsSource === 'static' ? selectedStaticKey : null;

	return (
		<Panel>
			<div css={styles.panel}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h1 css={styles.heading}>{formatMessage(messages.title)}</h1>

				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.text}>{formatMessage(messages.instructionsIntro)}</p>
				<ul css={styles.list}>
					<li>{formatMessage(messages.instructionsStatic)}</li>
					<li>
						<FormattedMessage
							{...messages.instructionsCustom}
							values={{
								cloudId: <code>cloudId</code>,
								orgId: <code>orgId</code>,
							}}
						/>
					</li>
				</ul>

				<div css={styles.collapsibleSections}>
					<CollapsibleSection
						header="Data Sampling"
						content={
							<div css={styles.collapsibleContent}>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.text}>{formatMessage(messages.description)}</p>

								<Switcher
									selectedTab={selectedTab}
									tabs={[
										{
											key: 'static',
											label: formatMessage(messages.staticTabLabel),
											icon: ListNumberedIcon,
										},
										{
											key: 'custom',
											label: formatMessage(messages.customTabLabel),
											icon: CustomizeIcon,
										},
									]}
									setTab={(tab) => {
										setSelectedTab(tab);
									}}
								/>

								{selectedTab === 'static' ? (
									<StaticDataList selectedKey={activeStaticKey} onSelect={handleStaticSelect} />
								) : (
									<CustomDataForm onSubmit={handleCustomSubmit} />
								)}
							</div>
						}
					/>

					<CollapsibleSection
						header={formatMessage(messages.featureGatesSectionTitle)}
						content={<FeatureGatesSection />}
					/>
				</div>

				<div css={styles.resetContainer}>
					<Button appearance="danger" onClick={handleReset} testId="debug-panel-reset">
						{formatMessage(messages.resetButton)}
					</Button>
				</div>
			</div>
		</Panel>
	);
}
